import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const { subdomainCompany } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Mot de passe réinitialisé. Vous pouvez vous connecter.");
      navigate("/login");
    } catch (err) {
      setError(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto animate-fade-up">
          <Logo className="mb-12" brand={subdomainCompany} />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Nouveau mot de passe</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Réinitialisation</h1>

          {!token ? (
            <>
              <p className="text-muted-foreground mb-8">Ce lien de réinitialisation est invalide. Demandez-en un nouveau.</p>
              <p className="text-sm text-destructive mb-6" data-testid="reset-invalid">Lien invalide.</p>
              <Link to="/forgot-password" className="text-primary font-medium hover:underline text-sm" data-testid="goto-forgot">
                Demander un nouveau lien
              </Link>
            </>
          ) : (
            <>
              <p className="text-muted-foreground mb-8">Choisissez un nouveau mot de passe pour votre compte.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="password">Nouveau mot de passe</Label>
                  <Input id="password" type="password" required value={password} data-testid="reset-password"
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 rounded-sm h-11" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                  <Input id="confirm" type="password" required value={confirm} data-testid="reset-confirm"
                    onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="mt-1.5 rounded-sm h-11" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-base font-semibold group">
                  {loading ? "Réinitialisation…" : "Réinitialiser le mot de passe"}
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </form>
            </>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login">Retour à la connexion</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-foreground">
        <img src="https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Acteio" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-3 max-w-md leading-tight">Des propositions commerciales qui closent plus vite.</h2>
          <p className="text-white/70 max-w-md">Créez, envoyez et suivez vos propositions premium. Boostez votre taux de signature.</p>
        </div>
      </div>
    </div>
  );
}
